import React, { useCallback, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';

interface UploadDropZoneProps {
  onFilesAdded: (files: File[]) => void;
  accept?: string;
  disabled?: boolean;
}

const UploadDropZone: React.FC<UploadDropZoneProps> = ({
  onFilesAdded,
  accept = '.pdf,.docx',
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Raw files go straight to the parent — validateFile() runs there,
  // mirroring the existing handleFileUpload pattern in your codebase.
  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!disabled) setIsDraggingOver(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback(() => {
    setIsDraggingOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDraggingOver(false);
      if (disabled) return;
      const dropped = Array.from(e.dataTransfer.files);
      if (dropped.length) onFilesAdded(dropped);
    },
    [disabled, onFilesAdded]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = Array.from(e.target.files ?? []);
      if (selected.length) onFilesAdded(selected);
      // Reset ref so the same file can be re-selected after being deleted
      if (inputRef.current) inputRef.current.value = '';
    },
    [onFilesAdded]
  );

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      sx={{
        border: '1.5px dashed',
        borderColor: isDraggingOver ? '#1A55F9' : '#C9CDD2',
        borderRadius: '8px',
        backgroundColor: isDraggingOver ? '#EEF3FF' : '#FAFBFC',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 3,
        px: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'border-color 0.2s, background-color 0.2s',
        '&:hover': disabled
          ? {}
          : {
              borderColor: '#1A55F9',
              backgroundColor: '#EEF3FF',
            },
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        hidden
        onChange={handleInputChange}
        disabled={disabled}
      />

      <FileUploadOutlinedIcon sx={{ color: '#1A55F9', fontSize: 32, mb: 1 }} />

      <Typography
        sx={{
          fontSize: 14,
          fontFamily: 'Poppins, sans-serif',
          color: '#2C2E50',
          fontWeight: 400,
        }}
      >
        Drag and drop or{' '}
        <Box component="span" sx={{ color: '#1A55F9', fontWeight: 500, cursor: 'pointer' }}>
          Browse
        </Box>
      </Typography>

      <Typography
        sx={{
          fontSize: 12,
          fontFamily: 'Poppins, sans-serif',
          color: '#8D9499',
          mt: 0.5,
        }}
      >
        Supported formats: PDF and Docx
      </Typography>
    </Box>
  );
};

export default UploadDropZone;
